import { useAppConstantsContext } from '@/context/AppConstants';
import { List, PureTextButton, Stack, Text, styled } from '@/theme/index';

const StyledText = styled(Text)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.grey[800],
  lineHeight: '1.4',
}));

const StyledPureTextButton = styled(PureTextButton)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.success.main,
  lineHeight: '1.4',
  minWidth: 'unset',
  padding: '0 0.25rem',
}));

const ParagraphText = styled(StyledText)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export const VehicleReportDisclaimer: React.FC = () => {
  const {
    images: { nmvtisLogo },
    links: {
      vehicleReportInquiryURL,
      nmvtisGlossaryURL,
      nmvtisStatesURL,
      nmvtisDataProvidersURL,
      nmvtisVehicleHistoryURL,
    },
  } = useAppConstantsContext();
  const currentYear = new Date().getFullYear();

  return (
    <Stack id="vehicle-report-disclaimer">
      <Text mb={2}>
        <Text fontSize="0.9rem" fontWeight="bold" component="span">
          Are you a California resident?
        </Text>
        <StyledPureTextButton
          variant="text"
          sx={{ fontSize: '0.9rem' }}
          onClick={() =>
            window.open(
              vehicleReportInquiryURL,
              '_blank',
              'noopener,noreferrer'
            )
          }
        >
          Report something missing or inaccurate.
        </StyledPureTextButton>
      </Text>
      <Text variant="h3" fontWeight="normal">
        Report Disclaimer
      </Text>
      <ParagraphText>
        This report does not contain detailed information regarding a vehicle's
        repair history nor information on all motor vehicles in the United
        States because some states are not yet providing their vehicle data to
        the system. Currently, the data provided by states is provided in a
        variety of time frames; while some states report and update data in
        "real-time" (as title transactions occur), other states send updates
        less frequently.
      </ParagraphText>
      <ParagraphText>
        Information on previous, significant vehicle damage may not be included
        in the system if the vehicle was never determined by an insurance
        company to be a "total loss" or branded by a state titling agency.
      </ParagraphText>
      <ParagraphText>
        A vehicle history report is NOT a substitute for an independent vehicle
        inspection. Before making a decision to purchase a vehicle, users are
        strongly encouraged to also obtain an independent vehicle inspection to
        ensure the vehicle does not have hidden damage.
      </ParagraphText>
      <ParagraphText>
        It may not currently include commercial vehicles if those vehicles are
        not included in a state's primary database for title records (in some
        states, those vehicles are managed by a separate state agency), although
        these records may be added at a later time.
      </ParagraphText>
      <ParagraphText>
        Brand types and definitions vary by state, but may provide useful
        information about the condition or prior use of the vehicle.
      </ParagraphText>
      <ParagraphText>
        The information used to compile this report is aggregated from various
        government agencies, non-profit organizations, and industry sources. The
        accuracy and reliability of the information supplied depends primarily
        on the reporting sources, and all entities involved in compiling this
        report accept no liability for any errors or omissions.
      </ParagraphText>
      <ParagraphText>
        Cost of Ownership data is ©{currentYear} Vincentric, LLC, and is
        provided by Vincentric AS IS without warranty or guarantee of any kind.
        Vincentric disclaims all warranties or conditions of any kind, expressed
        or implied, with respect to such data, including the implied warranties
        of merchantable quality and fitness for a particular purpose.
      </ParagraphText>
      <img src={nmvtisLogo} alt="NMVTIS Logo" height="90px" width="120px" />
      <Text variant="h3" fontWeight="normal">
        NMVTIS Disclaimer
      </Text>
      <ParagraphText>
        The National Motor Vehicle Title Information System (NMVTIS) is an
        electronic system that contains information on certain automobiles
        titled in the United States. NMVTIS is intended to serve as a reliable
        source of title and
        <StyledPureTextButton
          variant="text"
          onClick={() =>
            window.open(nmvtisGlossaryURL, '_blank', 'noopener,noreferrer')
          }
        >
          brand
        </StyledPureTextButton>
        history for automobiles, but it does not contain detailed information
        regarding a vehicle's repair history.
      </ParagraphText>
      <ParagraphText>
        All states, insurance companies, and junk and salvage yards are required
        by federal law to regularly report information to NMVTIS. However,
        NMVTIS does not contain information on all motor vehicles in the United
        States because
        <StyledPureTextButton
          variant="text"
          onClick={() =>
            window.open(nmvtisStatesURL, '_blank', 'noopener,noreferrer')
          }
        >
          some states
        </StyledPureTextButton>
        are not yet providing their vehicle data to the system. Currently, the
        data provided to NMVTIS by states is provided in a variety of time
        frames; while some states report and update NMVTIS data in "real-time"
        (as title transactions occur), other states send updates less
        frequently, such as once every 24 hours or within a period of days.
      </ParagraphText>
      <ParagraphText>
        Information on previous, significant vehicle damage may not be included
        in the system if the vehicle was never determined by an insurance
        company (or other appropriate entity) to be a "total loss" or branded by
        a state titling agency. Conversely, an insurance carrier may be required
        to report a "total loss" even if the vehicle's titling-state has not
        determined the vehicle to be "salvage" or "junk."
      </ParagraphText>
      <ParagraphText>
        A vehicle history report is NOT a substitute for an independent vehicle
        inspection. Before making a decision to purchase a vehicle, consumers
        are{' '}
        <strong>
          strongly encouraged to also obtain an independent vehicle inspection
        </strong>{' '}
        to ensure the vehicle does not have hidden damage. The
        <StyledPureTextButton
          variant="text"
          onClick={() =>
            window.open(nmvtisDataProvidersURL, '_blank', 'noopener,noreferrer')
          }
        >
          Approved NMVTIS Data Providers
        </StyledPureTextButton>
        (look for the NMVTIS logo) can include vehicle condition data from
        sources other than NMVTIS.
      </ParagraphText>
      <ParagraphText fontWeight="bold">
        Consumer Access Product Disclaimer
      </ParagraphText>
      <ParagraphText>
        NMVTIS data <strong>INCLUDES</strong> (as available by those entities
        required to report to the System):
      </ParagraphText>
      <List sx={{ listStyleType: 'disc', paddingLeft: 4 }}>
        <StyledText as="li" key={0}>
          Information from
          <StyledPureTextButton
            variant="text"
            onClick={() =>
              window.open(nmvtisStatesURL, '_blank', 'noopener,noreferrer')
            }
          >
            participating
          </StyledPureTextButton>
          state motor vehicle titling agencies.
        </StyledText>
        <StyledText as="li" key={1}>
          Information on automobiles, buses, trucks, motorcycles, recreational
          vehicles, motor homes, and tractors. NMVTIS may not currently include
          commercial vehicles if those vehicles are not included in a state's
          primary database for title records (in some states, those vehicles are
          managed by a separate state agency), although these records may be
          added at a later time.
        </StyledText>
        <StyledText as="li" key={2}>
          Information on "brands" applied to vehicles provided by participating
          state motor vehicle titling agencies. Brand types and definitions vary
          by state, but may provide useful information about the condition or
          prior use of the vehicle.
        </StyledText>
        <StyledText as="li" key={3}>
          Most recent odometer reading in the state's title record.
        </StyledText>
        <StyledText as="li" key={4}>
          Information from insurance companies, and auto recyclers, including
          junk and salvage yards, that is required by law to be reported to the
          system, beginning March 31, 2009. This information will include if the
          vehicle was determined to be a "total loss" by an insurance carrier.
        </StyledText>
        <StyledText as="li" key={5}>
          Information from junk and salvage yards receiving a "cash for clunker"
          vehicle traded-in under the Consumer Assistance to Recycle and Save
          Act of 2009 (CARS) Program.
        </StyledText>
      </List>
      <ParagraphText>
        Consumers are advised to visit
        <StyledPureTextButton
          variant="text"
          onClick={() =>
            window.open(
              nmvtisVehicleHistoryURL,
              '_blank',
              'noopener,noreferrer'
            )
          }
        >
          vehiclehistory.bja.ojp.gov
        </StyledPureTextButton>
        for details on how to interpret the information in the system and
        understand the meaning of various labels applied to vehicles by the
        participating state motor vehicle titling agencies.
      </ParagraphText>
    </Stack>
  );
};
